import {
  AnnouncementOutlined as AnnouncementOutlinedIcon,
  Check as CheckIcon,
  EditOutlined as EditOutlinedIcon,
  FileDownloadOutlined,
  MoreVert as MoreVertIcon,
  RefreshOutlined as RefreshOutlinedIcon,
} from '@mui/icons-material'
import {
  Button,
  Chip,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import { memo, useContext, useMemo, useRef, useState } from 'react'
import {
  MicroLearningDetailResponse,
  MicroLearningExportFormat,
  ReviewDecision,
  ScoreTooltip,
  UserRole,
} from '../../../api/models'
import ShareIcon from '../../../assets/share.svg'
import CustomDialog from '../../../components/dialogs/CustomDialog/CustomDialog'
import { ScoreDetailsPopover } from '../../../components/ScoreDetailsPopover'
import { useMenu } from '../../../hooks/useMenu'
import { AppStateContext } from '../../../state/AppProvider'
import { buildMicroLearningShareMessage, openTeamsShareDialog } from '../../../utils/teamsShare'
import styles from '../MicroLearningDetail.module.css'
import { formatDate, statusColor, statusLabel, statusVisual } from '../utils/detailFormatters'
import type { ManualEditData, ManualEditResult } from './ManualEditModal'
import ManualEditModal from './ManualEditModal/ManualEditModal'
import RegenerateModal from './RegenerateModal'

type DetailHeaderProps = {
  data: MicroLearningDetailResponse | null
  reviewDecision: ReviewDecision | null
  onApproveClick?: () => void
  onEditSuccess: (partial: Partial<MicroLearningDetailResponse>) => void
  onRegenerateSuccess?: () => void
  isExporting: boolean
  triggerExport: (format?: MicroLearningExportFormat) => Promise<void>
  onDismissClick?: () => void
}

const EMPTY_SCORE_TOOLTIP: ScoreTooltip = {
  experiment_codes: [],
  without_sales_evidence: [],
  missing_quantitative_results: [],
  kpi_mismatch: false,
}

type MicroLearningAction = 'approve' | 'edit' | 'refine' | 'dismiss'

const DetailHeader = ({
  data,
  reviewDecision,
  onApproveClick,
  onEditSuccess,
  onRegenerateSuccess,
  onDismissClick,
  isExporting,
  triggerExport,
}: DetailHeaderProps) => {
  const appStateContext = useContext(AppStateContext)
  const scoreRingRef = useRef<HTMLDivElement | null>(null)
  const { anchorEl, open: menuOpen, handleClick: handleMenuClick, handleClose: handleMenuClose } = useMenu()
  const [isScoreRingHovered, setIsScoreRingHovered] = useState(false)
  const [isScorePopoverHovered, setIsScorePopoverHovered] = useState(false)
  const [editStep, setEditStep] = useState<'closed' | 'warning' | 'editing'>('closed')
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false)
  const status = data?.status || 'not_processed'
  const score = data?.score?.total
  const scoreTooltip = data?.score?.tooltip || EMPTY_SCORE_TOOLTIP
  const statusTone = statusColor(status)
  const statusCardVisual = statusVisual(status)

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = setTimeout(() => {
      setIsScoreRingHovered(false)
      setIsScorePopoverHovered(false)
    }, 180)
  }

  const openEditFlow = () => {
    setEditStep(status === 'approved' ? 'warning' : 'editing')
  }

  const handleRefineClick = () => {
    setIsRegenerateModalOpen(true)
  }

  // Guard against older records where `potential_other_learnings` was computed
  // pre-selection and may no longer reflect the actual scale-potential content:
  // only treat the ML as a "Potential other learning" when at least one
  // completed experiment is actually present in `experiments_scale_potential`.
  const isPotentialOtherLearning =
    !!data?.potential_other_learnings && (data?.experiments_scale_potential ?? []).some(e => e.status === 'Completed')

  const canPerformAction = (action: MicroLearningAction): { allowed: boolean; reason?: string } => {
    const role = appStateContext?.state.user?.role
    const allowedRoles: UserRole[] = [UserRole.ADMIN, UserRole.DECODER]

    if (!role || !allowedRoles.includes(role)) {
      return { allowed: false, reason: 'You do not have permission to perform this action' }
    }

    if (status === 'regenerating') {
      return { allowed: false, reason: 'Micro-learning is currently being regenerated' }
    }

    if (action === 'approve' && isPotentialOtherLearning) {
      return {
        allowed: false,
        reason:
          'This cluster has more than 3 completed experiments and is a potential other learning — it does not require ML approval.',
      }
    }

    const statusRules: Record<MicroLearningAction, () => boolean> = {
      approve: () => status === 'pending_review',
      edit: () => !['dismissed', 'not_processed'].includes(status),
      refine: () => !['dismissed', 'not_processed'].includes(status),
      dismiss: () => status !== 'dismissed',
    }

    return { allowed: statusRules[action]() }
  }

  const isScorePopoverOpen = isScoreRingHovered || isScorePopoverHovered

  const editData: ManualEditData | null = useMemo(() => {
    if (!data) return null

    return {
      ml_id: data.ml_id,
      title: data.generation?.title || data.title || '',
      summary: data.generation?.summary || '',
      recommended_actions: data.generation?.recommended_actions || [],
      status,
    }
  }, [data, status])

  return (
    <div
      className={styles.headerCard}
      style={{
        backgroundColor: statusCardVisual.cardBackground,
        borderColor: statusCardVisual.cardBorder,
      }}>
      <div
        ref={scoreRingRef}
        className={styles.scoreRing}
        onMouseEnter={event => {
          void event
          clearCloseTimeout()
          setIsScoreRingHovered(true)
        }}
        onMouseLeave={scheduleClose}>
        <div
          className={styles.scoreCircle}
          data-tier={data?.score?.tier}>
          <div className={styles.scoreValue}>{status === 'not_processed' ? 'N/A' : score ?? '—'}</div>
          <span className={styles.scoreInlineLabel}>SCORE</span>
        </div>
      </div>

      <div className={styles.headerBody}>
        <div className={styles.headerMeta}>
          <div className={styles.headerBadges}>
            <Chip
              size="small"
              label={statusLabel(status)}
              className={styles.statusChip}
              style={{
                borderColor: statusTone,
                color: statusTone,
              }}
            />
            {isPotentialOtherLearning && (
              <Chip
                size="small"
                className={styles.scaleChip}
                label="Potential other learning"
              />
            )}
            {['approved', 'dismissed'].includes(status) && reviewDecision && (
              <span
                className={
                  status === 'dismissed' ? `${styles.auditText} ${styles.auditTextDismissed}` : styles.auditText
                }>
                {statusLabel(status)} by {reviewDecision.user?.name || '—'} on{' '}
                {formatDate(reviewDecision.decided_at || null)}
              </span>
            )}
          </div>
          <Button
            className={styles.moreActionButton}
            aria-label="More actions"
            aria-busy={isExporting || undefined}
            variant="text"
            size="small"
            onClick={handleMenuClick}
            disabled={isExporting}>
            {isExporting ? (
              <CircularProgress
                size={20}
                aria-label="Exporting"
              />
            ) : (
              <MoreVertIcon fontSize="small" />
            )}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}>
            {data?.generation == null
              ? [
                  <Tooltip
                    key="export-xlsx-disabled"
                    title="No generated content available for export">
                    <span tabIndex={0}>
                      <MenuItem
                        disabled
                        aria-disabled="true"
                        aria-label="Export as XLSX, dimmed">
                        <ListItemIcon>
                          <FileDownloadOutlined />
                        </ListItemIcon>
                        <ListItemText>Export as XLSX</ListItemText>
                      </MenuItem>
                    </span>
                  </Tooltip>,
                  <Tooltip
                    key="export-pptx-disabled"
                    title="No generated content available for export">
                    <span tabIndex={0}>
                      <MenuItem
                        disabled
                        aria-disabled="true"
                        aria-label="Export as PPTX, dimmed">
                        <ListItemIcon>
                          <FileDownloadOutlined />
                        </ListItemIcon>
                        <ListItemText>Export as PPTX</ListItemText>
                      </MenuItem>
                    </span>
                  </Tooltip>,
                ]
              : [
                  <MenuItem
                    key="export-xlsx"
                    onClick={() => {
                      handleMenuClose()
                      triggerExport('xlsx')
                    }}
                    aria-label="Export as XLSX">
                    <ListItemIcon>
                      <FileDownloadOutlined />
                    </ListItemIcon>
                    <ListItemText>Export as XLSX</ListItemText>
                  </MenuItem>,
                  <MenuItem
                    key="export-pptx"
                    onClick={() => {
                      handleMenuClose()
                      triggerExport('pptx')
                    }}
                    aria-label="Export as PPTX">
                    <ListItemIcon>
                      <FileDownloadOutlined />
                    </ListItemIcon>
                    <ListItemText>Export as PPTX</ListItemText>
                  </MenuItem>,
                ]}
            <MenuItem
              onClick={() => {
                handleMenuClose()
                const mlId = data?.ml_id ?? ''
                const title = data?.generation?.title ?? data?.title ?? ''
                const summary = data?.generation?.summary ?? ''
                const message = buildMicroLearningShareMessage(mlId, title, summary)
                const microLearningUrl = `${window.location.origin}/#/app/micro-learnings/${encodeURIComponent(mlId)}`
                openTeamsShareDialog(message, microLearningUrl)
              }}
              aria-label="Share to Teams">
              <ListItemIcon>
                <img
                  src={ShareIcon}
                  alt=""
                  width={20}
                  height={20}
                />
              </ListItemIcon>
              <ListItemText>Share to Teams</ListItemText>
            </MenuItem>
          </Menu>
        </div>

        <div className={styles.headerInfo}>
          <span className={styles.mlId}>{data?.ml_id || '—'}</span>
          <h1 className={styles.titleText}>{data?.generation?.title || data?.title || 'Micro-Learning'}</h1>
          <div className={styles.attributeGrid}>
            <div className={styles.attributeColumn}>
              <p className={styles.attributeLabel}>Master Hypothesis</p>
              <p className={styles.attributeValue}>{data?.master_hypothesis || data?.title || '—'}</p>
            </div>
            <div className={styles.attributeColumn}>
              <p className={styles.attributeLabel}>Experiments OU</p>
              <p className={styles.attributeValue}>{(data?.ous || []).join(', ') || '—'}</p>
            </div>
            {status === 'approved' && (reviewDecision?.published_to_ous ?? []).length > 0 && (
              <div className={styles.attributeColumn}>
                <p className={styles.attributeLabel}>Published to OU</p>
                <p className={styles.attributeValue}>{(reviewDecision?.published_to_ous ?? []).join(', ')}</p>
              </div>
            )}
          </div>
        </div>

        <div className={styles.actionRow}>
          {[
            {
              action: 'edit' as MicroLearningAction,
              label: 'Edit',
              icon: <EditOutlinedIcon />,
              className: styles.actionButton,
              variant: 'outlined' as const,
              onClick: openEditFlow,
            },
            {
              action: 'approve' as MicroLearningAction,
              label: 'Approve',
              icon: <CheckIcon />,
              className: `${styles.actionButton} ${styles.approveActionButton}`,
              variant: 'outlined' as const,
              onClick: onApproveClick,
            },
            {
              action: 'refine' as MicroLearningAction,
              label: 'Refine and Regenerate',
              icon: <AnnouncementOutlinedIcon />,
              className: `${styles.actionButton} ${styles.regenerateActionButton}`,
              variant: 'outlined' as const,
              onClick: handleRefineClick,
            },
            {
              action: 'dismiss' as MicroLearningAction,
              label: 'Dismiss',
              icon: <RefreshOutlinedIcon />,
              className: styles.dangerActionButton,
              variant: 'contained' as const,
              color: 'error' as const,
              onClick: onDismissClick,
            },
          ].map(({ action, label, icon, className, variant, ...rest }) => {
            const { allowed, reason } = canPerformAction(action)
            return (
              <Tooltip
                key={action}
                title={reason ?? ''}>
                <span>
                  <Button
                    variant={variant}
                    size="small"
                    className={className}
                    startIcon={icon}
                    disabled={!allowed}
                    {...rest}>
                    <span className={styles.actionButtonText}>{label}</span>
                  </Button>
                </span>
              </Tooltip>
            )
          })}
        </div>
      </div>
      <ScoreDetailsPopover
        open={isScorePopoverOpen}
        anchorEl={scoreRingRef.current}
        tooltip={scoreTooltip}
        notApplicable={status === 'not_processed'}
        hoverMode
        onClose={() => {
          clearCloseTimeout()
          setIsScoreRingHovered(false)
          setIsScorePopoverHovered(false)
        }}
        onMouseEnter={() => {
          clearCloseTimeout()
          setIsScorePopoverHovered(true)
        }}
        onMouseLeave={scheduleClose}
      />

      <CustomDialog
        title="Attention"
        open={editStep === 'warning'}
        setClose={() => setEditStep('closed')}
        primaryAction={() => setEditStep('editing')}
        secondaryAction={() => setEditStep('closed')}
        primaryActionButtonText="Continue"
        secondaryActionButtonText="Cancel"
        isBoldTitle>
        <Typography
          sx={{ fontFamily: 'Lato', fontWeight: 400, fontSize: '16px', lineHeight: '140%', color: '#1e1e1e' }}>
          This micro-learning is currently <strong>approved</strong>. Editing will revert it to Pending Review until
          re-approved.
        </Typography>
      </CustomDialog>

      {editData && (
        <ManualEditModal
          open={editStep === 'editing'}
          onClose={() => setEditStep('closed')}
          onSuccess={(result: ManualEditResult) => {
            onEditSuccess({
              status: result.new_status as MicroLearningDetailResponse['status'],
              generation: {
                ...data?.generation,
                title: result.title,
                summary: result.summary,
                recommended_actions: result.recommended_actions,
              },
            })
          }}
          microLearning={editData}
        />
      )}

      <RegenerateModal
        open={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        mlId={data?.ml_id || ''}
        data={data}
        onSuccess={onRegenerateSuccess || (() => {})}
      />
    </div>
  )
}

export default memo(DetailHeader)
